/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  deleteEventReservation,
  fetchArchivedEventReservations,
  updateEventReservationArchive,
} from "../../../services/EventReservationService";
import "./events.css";
import "./eventReservations.css";

const PAGE_SIZE = 15;

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatReservationSlot = (dateValue, timeValue) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";
  const dateLine = date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return timeValue ? `${dateLine} · ${timeValue}` : dateLine;
};

function ArchivedEventReservations() {
  const { t } = useTranslation();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [restoringId, setRestoringId] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const loadArchived = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchArchivedEventReservations({
        page: currentPage + 1,
        limit: PAGE_SIZE,
        search: searchQuery,
      });
      setReservations(data.reservations || []);
      setTotalItems(data.total || 0);
    } catch (error) {
      Swal.fire(
        t("eventReservationLoadError"),
        error?.response?.data?.message || error.message || t("eventReservationLoadError"),
        "error"
      );
      setReservations([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const handleRestore = async (reservation) => {
    const result = await Swal.fire({
      title: t("eventReservationRestoreConfirm"),
      text: t("eventReservationRestoreConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("eventReservationRestoreAction"),
      cancelButtonText: t("cancel"),
    });
    if (!result.isConfirmed) return;

    setRestoringId(reservation._id);
    try {
      await updateEventReservationArchive(reservation._id, false);
      await loadArchived();
      Swal.fire({
        icon: "success",
        title: t("eventReservationRestored"),
        text: t("eventReservationRestoredText"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("eventsError"),
        text: error?.response?.data?.message || error.message || t("eventReservationArchiveError"),
      });
    } finally {
      setRestoringId("");
    }
  };

  const handleDelete = async (reservation) => {
    const result = await Swal.fire({
      title: t("eventReservationDeleteConfirmTitle"),
      text: t("eventReservationDeleteConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
    });
    if (!result.isConfirmed) return;

    setDeletingId(reservation._id);
    try {
      await deleteEventReservation(reservation._id);
      await loadArchived();
      Swal.fire({
        icon: "success",
        title: t("eventReservationDeleted"),
        text: t("eventReservationDeletedText"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("eventsError"),
        text: error?.response?.data?.message || error.message || t("eventReservationDeleteError"),
      });
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="events-page ev-res-page">
      <div className="events-hero ev-res-hero">
        <div className="events-hero__glow" aria-hidden />
        <div className="events-hero__main">
          <div className="events-hero__content">
            <Link to="/events" className="ev-res-back" title={t("eventReservationBack")}>
              <i className="fa-solid fa-arrow-left" aria-hidden />
            </Link>
            <div className="ev-res-hero__icon">
              <i className="fa-solid fa-box-archive" aria-hidden />
            </div>
            <div className="ev-res-hero__text">
              <p className="ev-res-hero__eyebrow">{t("Archived")}</p>
              <h1 className="events-hero__title">{t("eventReservationArchivedPageTitle")}</h1>
              <p className="events-hero__subtitle">{t("eventReservationArchivedPageSubtitle")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="events-filters">
        <div
          className={`events-filters__bar ev-res-filters__bar${
            searchInput ? " events-filters__bar--has-clear" : ""
          }`}
        >
          <div className="events-filters__zone events-filters__zone--search">
            <span className="events-filters__label">{t("eventReservationSearch")}</span>
            <div className="events-search events-search--bar">
              <i className="fa-solid fa-magnifying-glass" aria-hidden />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("eventReservationSearchPlaceholder")}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="events-table-card ev-res-table-card">
        <div className="events-table-card__head">
          <div>
            <span>{t("Archived")}</span>
            <h3>{t("eventReservationArchivedTableTitle")}</h3>
          </div>
          <span className="events-table-card__count">
            {t("eventReservationTableCount", { count: totalItems })}
          </span>
        </div>

        {loading ? (
          <div className="ev-res-empty">{t("eventReservationLoading")}</div>
        ) : reservations.length === 0 ? (
          <div className="ev-res-empty">
            <strong>{t("eventReservationArchivedEmpty")}</strong>
            <p>{t("eventReservationArchivedEmptyText")}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="events-table ev-res-table">
              <thead>
                <tr>
                  <th>{t("eventsColEvent")}</th>
                  <th>{t("eventReservationColRequestedBy")}</th>
                  <th>{t("eventReservationColPersons")}</th>
                  <th>{t("eventReservationColSlot")}</th>
                  <th>{t("eventReservationColStatus")}</th>
                  <th>{t("eventReservationArchivedAt")}</th>
                  <th>{t("eventReservationColActions")}</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((reservation) => (
                  <tr key={reservation._id} className={`events-table-row ev-res-row ev-res-row--${reservation.status}`}>
                    <td className="ev-res-td">
                      <strong>{reservation.eventTitle || "—"}</strong>
                    </td>
                    <td className="ev-res-td">
                      <div>{reservation.firstName} {reservation.lastName}</div>
                      {reservation.email ? (
                        <div className="ev-res-archived-meta">{reservation.email}</div>
                      ) : null}
                    </td>
                    <td className="ev-res-td">{reservation.numberOfPersons || "—"}</td>
                    <td className="ev-res-td">
                      {formatReservationSlot(reservation.reservationDate, reservation.reservationTime)}
                    </td>
                    <td className="ev-res-td">
                      <span className={`ev-res-status ev-res-status--${reservation.status}`}>
                        <span className="ev-res-status__dot" aria-hidden />
                        {t(`eventReservationStatus_${reservation.status}`)}
                      </span>
                    </td>
                    <td className="ev-res-td">{formatDateTime(reservation.archivedAt)}</td>
                    <td className="ev-res-td ev-res-td--actions">
                      <div className="ev-res-archived-actions">
                        <button
                          type="button"
                          className="ev-res-archived-restore-btn"
                          disabled={restoringId === reservation._id || deletingId === reservation._id}
                          onClick={() => handleRestore(reservation)}
                        >
                          <i className="fa-solid fa-rotate-left" aria-hidden />
                          {t("eventReservationRestoreAction")}
                        </button>
                        <button
                          type="button"
                          className="ev-res-archived-delete-btn"
                          disabled={restoringId === reservation._id || deletingId === reservation._id}
                          onClick={() => handleDelete(reservation)}
                        >
                          <i className="fa-solid fa-trash" aria-hidden />
                          {t("delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 ? (
          <div className="events-pagination">
            <div className="events-pagination__info">
              {t("eventsPagination", {
                from: totalItems === 0 ? 0 : currentPage * PAGE_SIZE + 1,
                to: Math.min((currentPage + 1) * PAGE_SIZE, totalItems),
                total: totalItems,
              })}
            </div>
            <div className="events-pagination__buttons">
              <button
                type="button"
                className="events-page-btn"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage((page) => Math.max(0, page - 1))}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden />
              </button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`events-page-btn${currentPage === index ? " is-active" : ""}`}
                  onClick={() => setCurrentPage(index)}
                >
                  {index + 1}
                </button>
              ))}
              <button
                type="button"
                className="events-page-btn"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setCurrentPage((page) => Math.min(totalPages - 1, page + 1))}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ArchivedEventReservations;
